import React, { useEffect, useState } from 'react';

import { dateShort2 } from '../../../assets/contants/dates-fns';
import { formatPlainDate, plainDateFromEpochDay } from '../../../core/services/plain-date';
import { cssVars } from './css-vars';
import styles from './hover-guide.module.scss';

import type { TimeScale } from '../types';

export type HoverGuideProps = {
  /**
   * Ref to `.canvas` — the whole scrollable content, sidebar included, never `.scroller` (the
   * viewport). `getBoundingClientRect()` on `.canvas` already reflects the current horizontal
   * scroll: `.canvas` itself never moves, the viewport slides over it, so its rect's `left` is
   * however far the content has scrolled. The pointer's canvas-relative x falls out of that
   * subtraction directly — no `scrollLeft` to add back by hand.
   */
  canvasRef: React.RefObject<HTMLElement | null>;
  /**
   * Ref to `.scroller` — the VIEWPORT, `.canvas`'s parent. Used for one thing only: the sidebar
   * dead zone below. The row-header column is `position: sticky; left: 0`, so it sits at the
   * SCROLLPORT's left edge, not at the canvas origin — its canvas-space home (`0 … sidebarWidth`)
   * is only where it actually is while `scrollLeft` is 0. Testing the canvas-relative x against
   * `sidebarWidth` therefore tests the sidebar's unscrolled home, and once the chart is scrolled
   * the guide renders under a pointer that is over the sidebar (N2: reproduced in Chromium at
   * `scrollLeft: 8688`, `clientX: 20`, pill reading a column the pointer is nowhere near — the
   * line is hidden behind the opaque sidebar but the pill is at `--layer-tooltip` and paints over
   * it). Optional: a caller that does not pass it falls back to the canvas-relative comparison,
   * which is exactly right at `scrollLeft: 0` and no worse than before anywhere else.
   */
  scrollRef?: React.RefObject<HTMLElement | null>;
  /**
   * The chart's shared day<->pixel scale. `scale.dayAt` is the ONLY way the hovered day is
   * derived — never a second, hand-rolled `x / pxPerDay`; see `empty-row-add.tsx`'s identical
   * rule and its test of the same name.
   */
  scale: TimeScale;
  /** Same coordinate origin `xOf` / `dayAt` / `barLayout` all share — `state.range.startDay`. */
  windowStartDay: number;
  /** Width of the sticky row-header column. Below this x the pointer is over the sidebar, not a
   * day, so the guide has nothing to show. */
  sidebarWidth: number;
};

/**
 * A vertical line and a date pill that follow the pointer across the chart, as React state rather
 * than the old module's direct `.style.left` / `.innerHTML` writes inside a mousemove handler
 * (`gantt-chart/components/chart.tsx`'s `handleGuideLineMove`). Pure decoration: it commits
 * nothing, so it stays out of every commit path in this module.
 *
 * State lives HERE, not lifted to `GanttChart` or `useGanttState`: this fires on every mousemove
 * across the whole chart, and the only things that ever need to change are these two absolutely
 * positioned elements — the rows underneath do not. Keeping `x` local means a hover move
 * reconciles this small subtree only (two `<div>`s); lifting it into `GanttChart`'s own state
 * would re-render the whole chart — the virtualized row list included — on every pixel of pointer
 * movement, which is the "naive `setState` per mousemove" the brief warns against.
 *
 * The listener is a native `mousemove` / `mouseleave` pair added directly to `canvasRef.current`
 * — the same escape hatch `use-scroll-metrics.ts` uses for scroll — rather than a React
 * `onMouseMove` prop: this component does not own `.canvas` itself (`gantt-chart.tsx` does), so it
 * reaches it through a ref and renders as its sibling instead of wrapping it.
 */
export const HoverGuide: React.FC<HoverGuideProps> = ({
  canvasRef,
  scrollRef,
  scale,
  windowStartDay,
  sidebarWidth,
}) => {
  const [x, setX] = useState<number | null>(null);

  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return undefined;

    const handleMove = (event: MouseEvent): void => {
      const rect = element.getBoundingClientRect();
      // Canvas-relative: what the LINE and the PILL are drawn at, and the only coordinate the
      // day lookup may use — `.canvas` is the box `--hover-x` is resolved against.
      const canvasX = event.clientX - rect.left;

      // Scrollport-relative: the ONLY coordinate the sidebar dead zone may use, because the
      // sidebar is sticky at the scrollport's left edge rather than fixed at the canvas origin.
      // Read live off the element rather than taken from `useScrollMetrics`, which is a frame
      // behind the DOM by construction and would let the dead zone lag a fast horizontal scroll.
      // The two coordinates coincide only at `scrollLeft: 0`.
      const scroller = scrollRef?.current;
      const viewportX = scroller ? event.clientX - scroller.getBoundingClientRect().left : canvasX;

      // Over the sticky sidebar column, not a day: nothing to show there.
      setX(viewportX >= sidebarWidth ? canvasX : null);
    };

    const handleLeave = (): void => setX(null);

    element.addEventListener('mousemove', handleMove);
    element.addEventListener('mouseleave', handleLeave);

    return () => {
      element.removeEventListener('mousemove', handleMove);
      element.removeEventListener('mouseleave', handleLeave);
    };
  }, [canvasRef, scrollRef, sidebarWidth]);

  if (x === null) return null;

  // The inverse `xOf` already owns — never a second, hand-rolled division; see the prop doc above.
  const day = scale.dayAt(x - sidebarWidth, windowStartDay);
  const label = formatPlainDate(plainDateFromEpochDay(day), dateShort2);

  return (
    <>
      {/* Tracks the literal pointer pixel, not the day column it falls in — same as the old
          `DateVerticalGuideLine` (`gantt-chart/components/chart.tsx`), which moved continuously
          under the cursor while the LABEL below snapped to a day. */}
      <div
        data-testid="hover-guide-line"
        className={styles['line']}
        aria-hidden="true"
        style={cssVars({ '--hover-x': `${x}px` })}
      />

      {/* A zero-height sticky anchor (see `hover-guide.module.scss`'s `.pillLayer`): it takes no
          layout space of its own, restoring the old pill as the OVERLAY
          `--gantt-header-height`'s comment in `gantt-chart.module.scss` calls for, rather than
          growing that token back to 9.4rem. */}
      <div className={styles['pillLayer']} aria-hidden="true">
        <div
          data-testid="hover-guide-pill"
          className={styles['pill']}
          style={cssVars({ '--hover-x': `${x}px` })}
        >
          {label}
        </div>
      </div>
    </>
  );
};
